import type { ListBuilderColumn } from '../../types';
import { TRIP_STATUS_VALUE_MAPPING } from './shared';

/**
 * Trip name column with link.
 *
 * After Phase A rename, the table behind this list is `trip` (formerly
 * `route`), and trips don't have their own `name` — the display name comes
 * from the linked `route` (template). Column header is now "Route"
 * (referring to the template) and the field still queries `name` on the
 * `route` table because the post-rename `route` table IS the template.
 * If the list builder joins through `trip.fk_route__id` automatically the
 * value will populate; otherwise it shows "-".
 */
export const createTripNameColumn = (): ListBuilderColumn => ({
	id: 'route-name',
	headers: [{ id: 'route-name-header', text: 'Route' }],
	fields: [
		{
			id: 'name-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'name', objectTable: 'route' },
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
			link: { type: 'object', enabled: true },
		},
	],
	sortable: true,
	width: 200,
});

/**
 * Date column
 */
export const createDateColumn = (): ListBuilderColumn => ({
	id: 'date',
	headers: [{ id: 'date-header', text: 'Date' }],
	fields: [
		{
			id: 'date-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'date', objectTable: 'route' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'date',
			formatOptions: { dateStyle: 'short' },
		},
	],
	sortable: true,
	width: 120,
});

/**
 * Driver column
 */
export const createDriverColumn = (): ListBuilderColumn => ({
	id: 'driver',
	headers: [{ id: 'driver-header', text: 'Driver' }],
	fields: [
		{
			id: 'driver-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'driverName', objectTable: 'route' },
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
		},
	],
	sortable: true,
	width: 180,
});

/**
 * Vehicle column
 */
export const createVehicleColumn = (): ListBuilderColumn => ({
	id: 'vehicle',
	headers: [{ id: 'vehicle-header', text: 'Vehicle' }],
	fields: [
		{
			id: 'vehicle-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'vehicleName', objectTable: 'route' },
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
		},
	],
	sortable: true,
	width: 150,
});

/**
 * Status column with badge
 */
export const createStatusColumn = (): ListBuilderColumn => ({
	id: 'status',
	headers: [{ id: 'status-header', text: 'Status' }],
	fields: [
		{
			id: 'status-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'status', objectTable: 'route' },
			},
			styleConfig: {
				size: 'sm',
				valueMapping: [...TRIP_STATUS_VALUE_MAPPING],
			},
			displayStyle: 'badge',
		},
	],
	sortable: true,
	width: 130,
});

/**
 * Stops column
 */
export const createStopsColumn = (): ListBuilderColumn => ({
	id: 'stops',
	headers: [{ id: 'stops-header', text: 'Stops' }],
	fields: [
		{
			id: 'stops-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'totalStops', objectTable: 'route' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
		},
	],
	sortable: true,
	width: 100,
});

/**
 * Warehouse column
 */
export const createWarehouseColumn = (): ListBuilderColumn => ({
	id: 'warehouse',
	headers: [{ id: 'warehouse-header', text: 'Warehouse' }],
	fields: [
		{
			id: 'warehouse-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'warehouseName', objectTable: 'route' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'muted' },
			},
			displayStyle: 'text',
		},
	],
	sortable: true,
	width: 180,
});

/**
 * Create all route columns with date column
 */
export const createTripColumnsWithDate = (): Array<ListBuilderColumn> => [
	createTripNameColumn(),
	createDateColumn(),
	createDriverColumn(),
	createVehicleColumn(),
	createStatusColumn(),
	createStopsColumn(),
	createWarehouseColumn(),
];

/**
 * Create route columns without date column (for today's routes)
 */
export const createTripColumnsWithoutDate = (): Array<ListBuilderColumn> => [
	createTripNameColumn(),
	createDriverColumn(),
	createVehicleColumn(),
	createStatusColumn(),
	createStopsColumn(),
	createWarehouseColumn(),
];
